/**
 * 마법사의 계통도 자리 — 2단계(Scope)·3단계(Partition)가 같은 규칙으로 그린다.
 *
 * 전에는 두 단계가 따로 상자를 짜서 같은 그림이 2단계는 세로에 맞춰 줄고(747px) 3단계는 가로에 맞춰 커졌다(983px).
 * 여기서는 가로 몫(`flex: 1`)을 종횡비로 채우되 세로 상한(부모 높이)을 넘지 않는다 — 안쪽 SVG 가 종횡비를 지키며
 * 들어맞으므로 화면이 낮으면 그림이 함께 줄고 스크롤이 생기지 않는다. 글자는 계통 이름 층만 그린다
 * (`labels="system"`, ReactorSystemSVG) — 마법사에서 읽을 것은 «어느 계통이 켜졌나» 다.
 *
 * 그림 아래 각주 칸(범례·안내)은 고정 높이다(`DIAGRAM_FOOTER_HEIGHT`) — 단계마다 내용(안내 한 줄, 파티션 범례)이
 * 달라도 그림이 가져가는 몫이 같아야 2·3·4단계에서 그림의 크기와 자리가 같다. 내용이 칸을 넘으면 칸 안에서 스크롤한다.
 */
import React from 'react';
import { Box } from '@mui/material';
import { ReactorSystemSVG, type ReactorComponentId } from '@/features/project-picker/index';
import { SYSTEM_DIAGRAM } from '@/features/project-picker/systemDiagram.generated';
import { DIAGRAM_FOOTER_HEIGHT } from './wizardLayout';

interface DiagramPaneProps {
  highlightedComponents: ReactorComponentId[];
  dimmedComponents?: ReactorComponentId[];
  onComponentClick?: (id: ReactorComponentId) => void;
  /** 그림 아래 각주 칸에 들어가는 범례·안내 — 칸 높이는 고정(DIAGRAM_FOOTER_HEIGHT) */
  children?: React.ReactNode;
}

const DiagramPane: React.FC<DiagramPaneProps> = ({ highlightedComponents, dimmedComponents, onComponentClick, children }) => (
  <Box
    sx={{
      flex: { xs: '0 0 auto', md: '1 1 0' }, // 가로 행에선 오른쪽 열을 뺀 몫, 세로 스택에선 내용 높이
      minWidth: 0,
      minHeight: 0,
      alignSelf: { xs: 'stretch', md: 'flex-start' }, // 상자는 그림+각주만큼 — 오른쪽 열이 길어도 빈 상자를 늘리지 않고, 단계마다 같은 자리(위)에
      maxHeight: { xs: 'none', md: '100%' }, // 단, 높이는 단계 영역을 넘지 않는다 — 넘으면 종횡비 상자만 줄고 SVG 가 letterbox 된다(720p)
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      backgroundColor: 'var(--bg-content)',
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 2,
      p: 1.5,
    }}
  >
    <Box
      sx={{
        width: '100%',
        flex: '1 1 auto',
        minHeight: 0,
        aspectRatio: `${SYSTEM_DIAGRAM.viewBox[2]} / ${SYSTEM_DIAGRAM.viewBox[3]}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <ReactorSystemSVG
        highlightedComponents={highlightedComponents}
        dimmedComponents={dimmedComponents}
        onComponentClick={onComponentClick}
        labels="system"
        width="100%"
        height="100%"
      />
    </Box>
    <Box
      data-diagram-footer
      sx={{
        width: '100%',
        height: DIAGRAM_FOOTER_HEIGHT,
        flexShrink: 0,
        mt: 1,
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
      }}
    >
      {children}
    </Box>
  </Box>
);

export default DiagramPane;
